import React, { createContext, useContext } from 'react';

/* Shared foundation for the ClearWay explainer scene files.
 *
 * The story ships in two stage formats — desktop 1920x1080
 * (ClearWayExplainer) and mobile 1080x1920 (ClearWayExplainerMobile) —
 * that tell the same 56s story with the same product renders on the same
 * timeline, recomposed per aspect ratio. Everything stage-size-agnostic
 * lives here; captions, callouts, and scene layouts stay in the per-format
 * files.
 */

export const CL = (v, a, b) => Math.max(a, Math.min(b, v));

// ── Easing (Popmotion-style, t in [0,1] -> eased t) ────────────────
export const Easing = {
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeOutCubic: (t) => (--t) * t * t + 1,
  easeOutQuart: (t) => 1 - (--t) * t * t * t,
};
const E = Easing;

// ── Timeline (seconds) — identical across stage formats ─────────────
export const T = {
  intro: 0,
  problem: 6.5,
  reveal: 15,
  forward: 24,
  down: 34,
  alert: 42.5,
  outro: 49.5,
  end: 56,
};

export const EXPLAINER_DURATION = T.end;

/** Scene starts + labels, for external progress/navigation UI. */
export const EXPLAINER_SCENES = [
  { t: T.intro, label: 'Introducing ClearWay' },
  { t: T.problem, label: 'The problem' },
  { t: T.reveal, label: 'Meet ClearWay' },
  { t: T.forward, label: 'Forward detection' },
  { t: T.down, label: 'Ground scanning' },
  { t: T.alert, label: 'Instant alerts' },
  { t: T.outro, label: 'Smart mobility' },
];

// ── Brand tokens ─────────────────────────────────────────────────────
export const DISPLAY = "'Plus Jakarta Sans','SF Pro Display',sans-serif";
export const BODY = "'DM Sans',sans-serif";
export const MONO = "'SF Mono',Menlo,Consolas,monospace";
export const ACCENT = '#3b82f6';
export const SOFT = '#60a5fa';
export const INK = '#f5f5f7';
export const MUT = '#a0a0a0';
export const RED = '#f87171';
export const GREEN = '#34d399';
export const DIM = '#6b7280';
export const GRAD = {
  background: 'linear-gradient(180deg,#ffffff 0%,#a0a0a0 100%)',
  WebkitBackgroundClip: 'text', backgroundClip: 'text',
  color: 'transparent', WebkitTextFillColor: 'transparent',
};

// ── Time context ──────────────────────────────────────────────────────
export const TimeContext = createContext(0);
export const useTime = () => useContext(TimeContext);

// ── Helpers ───────────────────────────────────────────────────────────
// 0→1→0 envelope: fade in at t0 over inDur, out ending at t1 over outDur
export const env = (t, t0, inDur, t1, outDur) => {
  if (t < t0 || t > t1) return 0;
  return Math.min(
    E.easeOutCubic(CL((t - t0) / inDur, 0, 1)),
    E.easeOutCubic(CL((t1 - t) / outDur, 0, 1))
  );
};
export const rise = (t, t0, dur, dist) => (1 - E.easeOutCubic(CL((t - t0) / dur, 0, 1))) * dist;
// piecewise linear-with-ease keyframe sampler
export const kf = (ts, vs, e) => (t) => {
  if (t <= ts[0]) return vs[0];
  for (let i = 1; i < ts.length; i++) {
    if (t <= ts[i]) {
      const p = (t - ts[i - 1]) / (ts[i] - ts[i - 1]);
      return vs[i - 1] + (vs[i] - vs[i - 1]) * (e ? e(p) : p);
    }
  }
  return vs[vs.length - 1];
};

// ── Shared atoms ──────────────────────────────────────────────────────
export function Frame({ t0, t1, children, zoomFrom = 1, zoomTo = 1, origin = '50% 50%', fadeIn = 0.7, fadeOut = 0.7 }) {
  const t = useTime();
  const o = env(t, t0, fadeIn, t1, fadeOut);
  if (o <= 0) return null;
  const p = CL((t - t0) / (t1 - t0), 0, 1);
  const z = zoomFrom + (zoomTo - zoomFrom) * p;
  return (
    <div style={{ position: 'absolute', inset: 0, opacity: o, transform: `scale(${z})`, transformOrigin: origin }}>
      {children}
    </div>
  );
}

// Stage size and resting centre come from the per-format wrapper.
export function Radar({ w, h, cx, cy, t, maxR, base = 50, n = 4, alpha = 0.4, color = ACCENT, dot = true }) {
  const rings = [];
  for (let i = 0; i < n; i++) {
    const ph = ((t * 0.24) + i / n) % 1;
    const r = base + E.easeOutQuad(ph) * maxR;
    rings.push(
      <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={color}
        strokeOpacity={(1 - ph) * alpha} strokeWidth={1.5} />
    );
  }
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ position: 'absolute', left: 0, top: 0 }}>
      {rings}
      {dot && <circle cx={cx} cy={cy} r={4.5} fill={SOFT} opacity={0.9} />}
    </svg>
  );
}

const arcPath = (cx, cy, r, a0, a1) => {
  const p = (a) => [cx + r * Math.cos(a * Math.PI / 180), cy + r * Math.sin(a * Math.PI / 180)];
  const [x0, y0] = p(a0), [x1, y1] = p(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`;
};

export function PulseArcs({ cx, cy, t, startT = 0, period = 1.05, maxR = 560, dir = 0, spread = 64, color = ACCENT, alpha = 0.75, n = 3, w = 2.5 }) {
  if (t < startT) return null;
  const arcs = [];
  for (let i = 0; i < n; i++) {
    const ph = (((t - startT) / period) + i / n) % 1;
    const r = 24 + ph * maxR;
    const o = (1 - ph) * alpha * CL((t - startT) / 0.6, 0, 1);
    arcs.push(
      <path key={i} d={arcPath(cx, cy, r, dir - spread / 2, dir + spread / 2)}
        stroke={color} strokeWidth={w} fill="none" strokeOpacity={Math.max(o, 0)} strokeLinecap="round" />
    );
  }
  return <g>{arcs}</g>;
}

export function Rings({ cx, cy, t, startT = 0, period = 0.9, maxR = 170, color = SOFT, alpha = 0.8, n = 3, w = 2 }) {
  if (t < startT) return null;
  const rings = [];
  for (let i = 0; i < n; i++) {
    const ph = (((t - startT) / period) + i / n) % 1;
    rings.push(
      <circle key={i} cx={cx} cy={cy} r={10 + ph * maxR} fill="none" stroke={color}
        strokeOpacity={(1 - ph) * alpha * CL((t - startT) / 0.4, 0, 1)} strokeWidth={w} />
    );
  }
  return <g>{rings}</g>;
}

// ── Product render (front view) ────────────────────────────────────
function Transducer({ size = 46 }) {
  const inner = size * 0.68;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'radial-gradient(circle at 32% 28%, #fafafa, #b9bcc4 55%, #74777f)',
      boxShadow: '0 3px 8px rgba(0,0,0,0.35), inset 0 -2px 4px rgba(0,0,0,0.25)',
      display: 'grid', placeItems: 'center',
    }}>
      <div style={{
        width: inner, height: inner, borderRadius: '50%', background: '#15161a',
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(90deg, rgba(255,255,255,0.07) 0px, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 4px)',
        boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.9), inset 0 0 0 2px rgba(255,255,255,0.06)',
      }} />
    </div>
  );
}

export function Cane({ h = 760, glow = 0, sweep = -1, style }) {
  const boxW = 96, boxH = 138;
  const boxTop = h - 330;
  const redTop = boxTop + boxH - 6;
  const redH = (h - 34) - redTop;
  const sw = CL(sweep, 0, 1);
  const sweepLayer = sweep >= 0
    ? `linear-gradient(115deg, rgba(255,255,255,0) 42%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0) 58%), `
    : '';
  const sweepPos = sweep >= 0 ? `${(1 - sw) * 260 - 80}% 0%, ` : '';
  const sweepSize = sweep >= 0 ? '340% 100%, ' : '';
  return (
    <div style={{ position: 'relative', width: 160, height: h, ...style }}>
      {/* shaft */}
      <div style={{
        position: 'absolute', left: 73, top: 0, width: 14, height: h - 24, borderRadius: 8,
        backgroundImage: sweepLayer + 'linear-gradient(90deg,#84848c,#f7f7fa 42%,#ffffff 52%,#a8a8b2 100%)',
        backgroundSize: sweepSize + '100% 100%',
        backgroundPosition: sweepPos + '0% 0%',
        boxShadow: '10px 18px 30px rgba(0,0,0,0.45)',
      }} />
      {/* grip */}
      <div style={{
        position: 'absolute', left: 71.5, top: 10, width: 17, height: 170, borderRadius: 9,
        background: 'linear-gradient(90deg,#3f4046,#84858d 45%,#5c5d64 100%)',
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0px, rgba(0,0,0,0.25) 2px, transparent 2px, transparent 12px), linear-gradient(90deg,#3f4046,#84858d 45%,#5c5d64 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
      }} />
      {/* joint collars */}
      {[0.38, 0.56].map((f, i) => (
        <div key={i} style={{
          position: 'absolute', left: 71, top: h * f, width: 18, height: 10, borderRadius: 4,
          background: 'linear-gradient(90deg,#6e6e76,#d9d9df 45%,#8d8d95)',
        }} />
      ))}
      {/* red high-visibility sleeve */}
      <div style={{
        position: 'absolute', left: 72, top: redTop, width: 16, height: Math.max(redH, 0), borderRadius: 4,
        backgroundImage: sweepLayer + 'linear-gradient(90deg,#7f1d1d,#ef4444 42%,#fca5a5 52%,#991b1b)',
        backgroundSize: sweepSize + '100% 100%',
        backgroundPosition: sweepPos + '0% 0%',
      }} />
      {/* sensor module */}
      <div style={{
        position: 'absolute', left: 80 - boxW / 2, top: boxTop, width: boxW, height: boxH, borderRadius: 18,
        backgroundImage: sweepLayer + 'linear-gradient(145deg,#ffffff 0%,#eceef2 55%,#d3d5db 100%)',
        backgroundSize: sweepSize + '100% 100%',
        backgroundPosition: sweepPos + '0% 0%',
        boxShadow: `0 26px 52px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.9), 0 0 ${44 * glow}px rgba(59,130,246,${0.55 * glow})`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
      }}>
        <Transducer />
        <Transducer />
        <div style={{
          position: 'absolute', bottom: 9, left: '50%', marginLeft: -3, width: 6, height: 6, borderRadius: '50%',
          background: SOFT, boxShadow: `0 0 ${8 + 12 * glow}px ${SOFT}`, opacity: 0.45 + 0.55 * glow,
        }} />
      </div>
      {/* tip ball */}
      <div style={{
        position: 'absolute', left: 68, top: h - 26, width: 24, height: 24, borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #ffffff, #c9c9d1 65%, #93939c)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
      }} />
    </div>
  );
}

// Side view (walking scenes). Tip pivots at (x+80, y-10); handle trails left.
export function SideCane({ x, y, t, tilt = -20, walking = true }) {
  const sway = walking ? Math.sin(t * 2.1) * 1.6 : 0;
  const bob = walking ? Math.sin(t * 4.2) * 3 : 0;
  return (
    <div style={{
      position: 'absolute', left: x, top: y - 720 + bob, width: 160, height: 720,
      transform: `rotate(${tilt + sway}deg)`, transformOrigin: '80px 710px',
    }}>
      <div style={{
        position: 'absolute', left: 74, top: 0, width: 12, height: 566, borderRadius: 6,
        background: 'linear-gradient(90deg,#8e8e96,#ffffff 50%,#a8a8b2)',
      }} />
      <div style={{
        position: 'absolute', left: 72.5, top: 4, width: 15, height: 140, borderRadius: 8,
        background: 'linear-gradient(90deg,#3f4046,#84858d 45%,#5c5d64)',
      }} />
      {/* sensor module, transducers facing forward (right) */}
      <div style={{
        position: 'absolute', left: 52, top: 520, width: 58, height: 106, borderRadius: 13,
        background: 'linear-gradient(180deg,#ffffff,#dcdee4)',
        boxShadow: '0 16px 32px rgba(0,0,0,0.45)',
      }}>
        <div style={{ position: 'absolute', right: -7, top: 16, width: 10, height: 26, borderRadius: '3px 7px 7px 3px', background: 'linear-gradient(180deg,#c7cad2,#75787f)' }} />
        <div style={{ position: 'absolute', right: -7, top: 56, width: 10, height: 26, borderRadius: '3px 7px 7px 3px', background: 'linear-gradient(180deg,#c7cad2,#75787f)' }} />
        <div style={{ position: 'absolute', left: 8, top: 46, width: 5, height: 5, borderRadius: '50%', background: SOFT, boxShadow: `0 0 8px ${SOFT}` }} />
      </div>
      <div style={{
        position: 'absolute', left: 75, top: 626, width: 12, height: 64, borderRadius: 3,
        background: 'linear-gradient(90deg,#7f1d1d,#ef4444 45%,#991b1b)',
      }} />
      <div style={{
        position: 'absolute', left: 70, top: 688, width: 20, height: 20, borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #ffffff, #b9b9c2)',
      }} />
    </div>
  );
}

export function Silhouette({ lt, delay, x, y, w, h }) {
  const o = env(lt, delay, 1.4, T.reveal - T.problem, 0.6);
  const grow = 1 + E.easeInQuad(CL((lt - delay) / 7, 0, 1)) * 0.35;
  const flash = Math.max(0, Math.sin((lt - delay) * 1.1)) * 0.25;
  if (o <= 0) return null;
  return (
    <div style={{
      position: 'absolute', left: x, top: y, width: w, height: h, opacity: o * 0.9,
      transform: `scale(${grow})`, transformOrigin: '50% 100%',
      background: 'linear-gradient(180deg, #17181b, #101114)', borderRadius: 10,
      border: `1px solid rgba(248,113,113,${0.08 + flash})`,
      boxShadow: '0 30px 60px rgba(0,0,0,0.6)', filter: 'blur(0.5px)',
    }} />
  );
}
